import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button, Field, Input, makeStyles } from "@fluentui/react-components";
import { AddRegular, DeleteRegular } from "@fluentui/react-icons";
import { validate } from "uuid";

// CiLayout.jsx
import { useParams } from "react-router-dom";

// Business logic
import { useCiQuery } from "../hooks/useCiQueries";
import { useCreateCi, useUpdateCi } from "../hooks/useCiMutations";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { ciSchema } from "@shared/schemas";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";
import { readCiStatuses } from "../../cistatuses/services/ciStatusApi";
import { readCiTypes } from "../../citypes/services/ciTypesApi";
import { readSites } from "../../sites/services/siteApi";
import { readUsers } from "../../users/services/userApi";
import { getDefaultSubnetMask } from "@shared/utils/ip";

// Components
import "../../../styles/page.css";
import CiTypeImage from "../../citypes/components/CiTypeImage";
import { GenericComboBox } from "../../../shared/components/GenericComboBox";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { useNotify } from "../../../shared/components/SuperToast";
import { useTranslation } from "react-i18next";
import CiRelationDrawer from "../../ciRelations/components/CiRelationDrawer";
import { useCiRelationsQuery } from "../../ciRelations/hooks/useCiRelationQueries";
import { useDeleteCiRelation } from "../../ciRelations/hooks/useCiRelationMutations";
import {
  SelectedCiRelationsContext,
  useSelectedCiRelationsContext,
} from "../../ciRelations/context/SelectedCiRelationsContext";
import Spinner from "../../../shared/components/Spinner";

// Removed unused apiUrl

const useStyles = makeStyles({
  field: {
    display: "grid",
    gridRowGap: "4px",
    margin: "6px 0",
  },
});

const CiEditor = ({ newCi = false }) => {
  const { t } = useTranslation();
  const { publicId } = useParams();
  const [CurrentCi, setCurrentCi] = useState(null);
  const [isRelationCreatorOpen, setIsRelationCreatorOpen] = useState(false);
  const [isIncomingRelationCreatorOpen, setIsIncomingRelationCreatorOpen] = useState(false);
  const [isRelationEditorOpen, setIsRelationEditorOpen] = useState(false);
  const styles = useStyles();
  const navigate = useNavigate();
  const { notify } = useNotify();
  const queryClient = useQueryClient();

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateCi = useItemValidation(ciSchema.$defs.ci);

  // Errors
  const [errors, setErrors] = useState([]);

  const isExistingCi = !newCi && validate(publicId);

  // Queries
  const { data: ciData, isLoading, error: ciError } = useCiQuery(publicId, isExistingCi);
  const { data: ciTypesData = [], error: ciTypesError } = useItemQuery([`citypes`], () => readCiTypes({}), true);
  const { data: sitesData = [], error: sitesError } = useItemQuery([`sites`], () => readSites({}), true);
  const { data: usersData = [], error: usersError } = useItemQuery([`users`], () => readUsers({}), true);
  const { data: ciStatusesData = [], error: ciStatusesError } = useItemQuery(
    [`cistatuses`],
    () => readCiStatuses({}),
    true,
  );
  const { data: outgoingRelations = [] } = useCiRelationsQuery(
    { public_source_id: ciData?.public_id },
    isExistingCi && !!ciData?.id,
  );
  const { data: incomingRelations = [] } = useCiRelationsQuery(
    { public_target_id: ciData?.public_id },
    isExistingCi && !!ciData?.id,
  );

  // Delete relation mutations
  const { mutate: deleteOutgoingRelation } = useDeleteCiRelation(
    ["ci_relations", { public_source_id: ciData?.public_id }],
    () => notify(t("item.messages.deleted", { name: t("ci.relation", { defaultValue: "Relation" }) }), "success"),
    (error) => notify(error.message, "error"),
  );
  const { mutate: deleteIncomingRelation } = useDeleteCiRelation(
    ["ci_relations", { target_id: ciData?.public_id }],
    () => notify(t("item.messages.deleted", { name: t("ci.relation", { defaultValue: "Relation" }) }), "success"),
    (error) => notify(error.message, "error"),
  );

  // Mutations
  const {
    mutate: createCiMutate,
    error: createCiError,
    isPending: isCreating,
  } = useCreateCi(
    "cis",
    (data) => {
      notify(t("item.messages.created", { name: data.name }), "success");
      handleClose();
    },
    null,
    false,
  );
  const {
    mutate: updateCiMutate,
    error: updateCiError,
    isPending: isUpdating,
  } = useUpdateCi(
    "cis",
    (data) => {
      notify(t("item.messages.updated", { name: data.name }), "success");
      handleClose();
    },
    null,
    false,
  );

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (ciError) {
      newErrors.push({ message: ciError?.message || t("generic.error"), type: "error", details: ciError });
    }
    if (ciTypesError) {
      newErrors.push({ message: ciTypesError?.message || t("generic.error"), type: "error", details: ciTypesError });
    }
    if (sitesError) {
      newErrors.push({ message: sitesError?.message || t("generic.error"), type: "error", details: sitesError });
    }
    if (usersError) {
      newErrors.push({ message: usersError?.message || t("generic.error"), type: "error", details: usersError });
    }
    if (ciStatusesError) {
      newErrors.push({
        message: ciStatusesError?.message || t("generic.error"),
        type: "error",
        details: ciStatusesError,
      });
    }
    if (createCiError) {
      newErrors.push({ message: createCiError?.message || t("generic.error"), type: "error", details: createCiError });
    }
    if (updateCiError) {
      newErrors.push({ message: updateCiError?.message || t("generic.error"), type: "error", details: updateCiError });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [ciError, ciTypesError, sitesError, usersError, ciStatusesError, createCiError, updateCiError, t]);

  useEffect(() => {
    if (newCi) {
      const newCi = {};
      setCurrentCi(newCi);
      setValidationErrors(validateCi(newCi));
    } else if (ciData) {
      setCurrentCi(ciData);
      const errors = validateCi(ciData);
      setValidationErrors(errors);
    }
  }, [publicId, ciData, newCi]);

  // Handlers
  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["ci", publicId] });
    setCurrentCi(null);
    setValidationErrors({});
    setErrors([]);
    navigate("/ci");
  };

  const updateField = (field, value) => {
    const updated = { ...CurrentCi, [field]: typeof value === "string" && value === "" ? null : value };
    if (field === "type_id") {
      const selectedType = ciTypesData.find((type) => type.id === value);
      if (selectedType) {
        updated.icon_url = selectedType.icon_url;
      }
    }
    if (field === "ip" && value && !updated.mask) {
      const defaultMask = getDefaultSubnetMask(value);
      if (defaultMask) {
        updated.mask = defaultMask;
      }
    }
    setCurrentCi(updated);
    setValidationErrors(validateCi(updated));
  };

  if (isLoading) {
    return <div>Loading...</div>;
  } else {
    const optionTemplate = (ciType) => {
      return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: `${ciType.level * 7}px` }} />
          {ciType?.public_icon_id && <CiTypeImage ciType={ciType} height="20px" />}
          <span>{ciType.name}</span>
        </div>
      );
    };

    return (
      <div className="page">
        <header>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
            {CurrentCi?.public_icon_id && (
              <CiTypeImage ciType={ciTypesData.find((type) => type.id === CurrentCi?.type_id)} height="32px" />
            )}
            <h1>{isExistingCi ? CurrentCi?.name : t("ci.new")}</h1>
          </span>
          <hr />
        </header>
        <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
        <main>
          <div style={{ display: "flex", flexDirection: "row", height: "100%" }}>
            <div
              style={{
                display: "flex",
                flex: 1,
                flexDirection: "column",
                padding: "20px",
                minHeight: 0,
                overflow: "auto",
              }}
            >
              <div style={{ width: "250px" }}>
                <Field
                  className={styles.field}
                  label={t("generic.name")}
                  validationState={validationErrors.name ? "error" : undefined}
                  required
                >
                  <Input
                    id="name"
                    value={CurrentCi?.name ?? ""}
                    onChange={(e) => updateField("name", e.target.value)}
                    maxLength="50"
                  />
                </Field>
                <Field
                  className={styles.field}
                  label={t("generic.type")}
                  validationState={validationErrors.type_id ? "error" : undefined}
                  required
                >
                  <GenericComboBox
                    items={ciTypesData || []}
                    value={CurrentCi?.type_id}
                    valueKey="id"
                    displayKey="name"
                    iconKey="icon_url"
                    placeholder="Select CI Type"
                    itemTemplate={optionTemplate}
                    onOptionSelect={(newTypeId) => updateField("type_id", newTypeId)}
                  />
                </Field>
                <Field
                  className={styles.field}
                  label={t("generic.status")}
                  validationState={validationErrors.status_id ? "error" : undefined}
                >
                  <GenericComboBox
                    items={ciStatusesData || []}
                    value={CurrentCi?.status_id}
                    valueKey="id"
                    displayKey="name"
                    placeholder="Select Status"
                    onOptionSelect={(newStatusId) => updateField("status_id", newStatusId)}
                  />
                </Field>
                <Field
                  className={styles.field}
                  label={t("generic.serialNumber")}
                  validationState={validationErrors.sn ? "error" : undefined}
                >
                  <Input
                    id="sn"
                    value={CurrentCi?.sn ?? ""}
                    onChange={(e) => updateField("sn", e.target.value)}
                    maxLength="50"
                  />
                </Field>
                <Field
                  className={styles.field}
                  label={t("generic.modelNumber")}
                  validationState={validationErrors.mn ? "error" : undefined}
                >
                  <Input
                    id="mn"
                    value={CurrentCi?.mn ?? ""}
                    onChange={(e) => updateField("mn", e.target.value)}
                    maxLength="50"
                  />
                </Field>
                <Field className={styles.field} label="IP" validationState={validationErrors.ip ? "error" : undefined}>
                  <Input
                    id="ip"
                    value={CurrentCi?.ip ?? ""}
                    onChange={(e) => updateField("ip", e.target.value)}
                    maxLength="15"
                  />
                </Field>
                <Field
                  className={styles.field}
                  label={t("generic.mask")}
                  validationState={validationErrors.mask ? "error" : undefined}
                >
                  <Input
                    id="mask"
                    value={CurrentCi?.mask ?? ""}
                    onChange={(e) => updateField("mask", e.target.value)}
                    maxLength="15"
                  />
                </Field>
                <Field
                  className={styles.field}
                  label={t("generic.owner")}
                  validationState={validationErrors.owner_id ? "error" : undefined}
                >
                  <GenericComboBox
                    items={usersData || []}
                    value={CurrentCi?.owner_id}
                    valueKey="id"
                    displayKey="name"
                    placeholder="Select Owner"
                    includeEmptyOption
                    onOptionSelect={(newOwnerId) => updateField("owner_id", newOwnerId)}
                  />
                </Field>
                <Field
                  className={styles.field}
                  label={t("generic.site")}
                  validationState={validationErrors.site_id ? "error" : undefined}
                >
                  <GenericComboBox
                    items={sitesData || []}
                    value={CurrentCi?.site_id}
                    valueKey="id"
                    displayKey="name"
                    placeholder="Select Site"
                    onOptionSelect={(newSiteId) => updateField("site_id", newSiteId)}
                  />
                </Field>
              </div>
              {validationErrors && Object.keys(validationErrors).length > 0 && (
                <DataValidationMessageBar style={{ margin: "20px 0" }} validationErrors={validationErrors} />
              )}
            </div>
            <div style={{ width: "50px" }} />
            <SelectedCiRelationsContext>
              <RelationsSection
                t={t}
                CurrentCi={CurrentCi}
                ciTypesData={ciTypesData}
                validationErrors={validationErrors}
                incomingRelations={incomingRelations}
                outgoingRelations={outgoingRelations}
                onDeleteOutgoing={(rel) => {
                  if (
                    window.confirm(t("item.questions.delete", { name: `${rel.relation_type_name} ${rel.target_name}` }))
                  ) {
                    deleteOutgoingRelation(rel.id);
                  }
                }}
                onDeleteIncoming={(rel) => {
                  if (
                    window.confirm(
                      t("item.questions.delete", { name: `${rel.relation_type_reverse_name} ${rel.source_name}` }),
                    )
                  ) {
                    deleteIncomingRelation(rel.id);
                  }
                }}
                setIsRelationCreatorOpen={setIsRelationCreatorOpen}
                setIsIncomingRelationCreatorOpen={setIsIncomingRelationCreatorOpen}
                setIsRelationEditorOpen={setIsRelationEditorOpen}
              />
              {/* Relation Drawer for creating outgoing relations */}
              <CiRelationDrawer
                isOpen={isRelationCreatorOpen}
                setIsOpen={setIsRelationCreatorOpen}
                newItem={true}
                fixedSourceId={CurrentCi?.id}
                sourceQuery={["ci_relations", { source_id: publicId }]}
              />
              {/* Relation Drawer for creating incoming relations */}
              <CiRelationDrawer
                isOpen={isIncomingRelationCreatorOpen}
                setIsOpen={setIsIncomingRelationCreatorOpen}
                newItem={true}
                fixedTargetId={CurrentCi?.id}
                sourceQuery={["ci_relations", { target_id: publicId }]}
              />
              {/* Relation Drawer for editing existing relations */}
              <CiRelationDrawer isOpen={isRelationEditorOpen} setIsOpen={setIsRelationEditorOpen} />
            </SelectedCiRelationsContext>
          </div>
        </main>

        <footer>
          <div className="footerButtons">
            <Button
              icon={isCreating || isUpdating ? <Spinner size="tiny" /> : undefined}
              appearance="primary"
              disabled={Object.keys(validationErrors).length > 0}
              onClick={() =>
                CurrentCi?.id > 0 ? updateCiMutate(CurrentCi, handleClose) : createCiMutate(CurrentCi, handleClose)
              }
            >
              {t(isExistingCi ? "generic.update" : "generic.create")}
            </Button>
            <Button appearance="secondary" onClick={() => navigate("/ci")}>
              {t("generic.cancel")}
            </Button>
          </div>
        </footer>
      </div>
    );
  }
};

export default CiEditor;

function RelationsSection({
  t,
  CurrentCi,
  ciTypesData,
  validationErrors,
  incomingRelations,
  outgoingRelations,
  onDeleteOutgoing,
  onDeleteIncoming,
  setIsRelationCreatorOpen,
  setIsIncomingRelationCreatorOpen,
  setIsRelationEditorOpen,
}) {
  const { setValue: setSelectedRelations } = useSelectedCiRelationsContext();

  const openEditorForRelation = (relId) => {
    setSelectedRelations([{ public_id: relId }]);
    setIsRelationEditorOpen(true);
  };

  return (
    <aside style={{ display: "flex", flex: "0 0 350px", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "8px 12px" }}>
        {validationErrors && Object.keys(validationErrors).length > 0 && (
          <DataValidationMessageBar style={{ margin: "0 0 12px 0" }} validationErrors={validationErrors} />
        )}
        <div style={{ padding: "8px 0" }}>
          <h3 style={{ margin: "0 0 12px 0" }}>{t("generic.details", { defaultValue: "Details" })}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "14px" }}>
            <div>
              <span style={{ fontWeight: "600" }}>
                {t("generic.creationDate", { defaultValue: "Creation date" })}:{" "}
              </span>
              <span style={{ color: "#666" }}>
                {CurrentCi?.created_at ? new Date(CurrentCi.created_at).toLocaleString() : "-"}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: "600" }}>
                {t("generic.modificationDate", { defaultValue: "Modification date" })}:{" "}
              </span>
              <span style={{ color: "#666" }}>
                {CurrentCi?.modified_at ? new Date(CurrentCi.modified_at).toLocaleString() : "-"}
              </span>
            </div>
          </div>
        </div>
        {CurrentCi?.id && (
          <div style={{ padding: "8px 0", marginTop: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0 }}>{t("ci.outgoingRelations", { defaultValue: "Outgoing relations" })}</h3>
              <Button
                size="small"
                icon={<AddRegular />}
                onClick={() => setIsRelationCreatorOpen(true)}
                disabled={!CurrentCi?.id}
              >
                {t("generic.add", { defaultValue: "Add" })}
              </Button>
            </div>
            <div style={{ marginTop: "8px" }}>
              {outgoingRelations.length === 0 ? (
                <div style={{ color: "#666", fontSize: "14px" }}>
                  {t("ci.noOutgoingRelations", { defaultValue: "No outgoing relations yet" })}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
                  {outgoingRelations.map((rel) => {
                    const targetType = ciTypesData?.find((t) => t.id === rel.target_type);
                    return (
                      <div
                        key={rel.id}
                        style={{ padding: "2px 0", cursor: "pointer" }}
                        onClick={() => openEditorForRelation(rel.public_id)}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                            {targetType && <CiTypeImage ciType={targetType} height="16px" />}
                            {rel.relation_type_name} {rel.target_name}
                          </span>
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<DeleteRegular />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteOutgoing?.(rel);
                            }}
                          >
                            {t("generic.delete")}
                          </Button>
                        </div>
                        {rel.comments && (
                          <div style={{ color: "#666", fontSize: "12px", marginTop: "2px" }}>{rel.comments}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h3 style={{ margin: 0 }}>{t("ci.incomingRelations", { defaultValue: "Incoming relations" })}</h3>
                <Button
                  size="small"
                  icon={<AddRegular />}
                  onClick={() => setIsIncomingRelationCreatorOpen(true)}
                  disabled={!CurrentCi?.id}
                >
                  {t("generic.add", { defaultValue: "Add" })}
                </Button>
              </div>
              <div style={{ marginTop: "12px" }}>
                {incomingRelations.length === 0 ? (
                  <div style={{ color: "#666", fontSize: "14px" }}>
                    {t("ci.noIncomingRelations", { defaultValue: "No incoming relations yet" })}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
                    {incomingRelations.map((rel) => {
                      const ciType = ciTypesData?.find((t) => t.id === rel.source_type);
                      return (
                        <div
                          key={rel.public_id}
                          style={{ padding: "4px 0", cursor: "pointer" }}
                          onClick={() => openEditorForRelation(rel.public_id)}
                        >
                          <div
                            style={{
                              fontSize: "14px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              justifyContent: "space-between",
                            }}
                          >
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                              {ciType && <CiTypeImage ciType={ciType} height="16px" />}
                              {rel.relation_type_reverse_name} {rel.source_name}
                            </span>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<DeleteRegular />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteIncoming?.(rel);
                              }}
                            >
                              {t("generic.delete")}
                            </Button>
                          </div>
                          {rel.comments && (
                            <div style={{ color: "#666", fontSize: "12px", marginTop: "2px" }}>{rel.comments}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
